/** One slice / bar of an insight chart. */
export interface InsightChartRow {
  /** Stable id of the entity (status value, type id…), emitted on select. */
  key: string;
  label: string;
  value: number;
  /** Colour of the entity; keep it tied to the entity, not its rank, so filters never repaint it. */
  color: string;
}

/** A row with its derived share of the total and, for bars, its length against the largest row. */
export interface InsightChartRowView extends InsightChartRow {
  percentage: string;
  /** 0-100, relative to the largest row. */
  share: number;
}

/*
 * Categorical order for insight charts (validated: every adjacent pair clears the CVD and
 * normal-vision floors). Give an entity its slot by its place in a fixed list, never its rank;
 * entities past the last slot use INSIGHT_CHART_FALLBACK_COLOR instead of reusing a hue.
 */
export const INSIGHT_CHART_COLORS: readonly string[] = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#6250d6',
  '#e34948',
];

export const INSIGHT_CHART_FALLBACK_COLOR = '#9ca3af';

/** Colour for the entity at `slot` in its fixed list. */
export function insightChartColor(slot: number): string {
  return slot >= 0
    ? (INSIGHT_CHART_COLORS[slot] ?? INSIGHT_CHART_FALLBACK_COLOR)
    : INSIGHT_CHART_FALLBACK_COLOR;
}

/** Whole percent, with "<1%" so a small non-zero slice never reads as zero. */
export function formatInsightShare(value: number, total: number): string {
  if (!total || !value) {
    return '0%';
  }
  const percent = (value / total) * 100;
  return percent < 1 ? '<1%' : `${Math.round(percent)}%`;
}

export function toInsightRowViews(
  rows: InsightChartRow[]
): InsightChartRowView[] {
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  const max = Math.max(0, ...rows.map(row => row.value));
  return rows.map(row => ({
    ...row,
    percentage: formatInsightShare(row.value, total),
    share: max ? (row.value / max) * 100 : 0,
  }));
}
